import "@/App.css";
import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  Truck,
  Shield,
  Settings,
  Zap,
  MapPin,
  Mail,
  User,
  ArrowRight,
  CheckCircle2,
  Clock,
  Star,
  Menu,
  X,
  Phone,
  Lock
} from "lucide-react";

const LOGO_URL = "/zmk-logo-trimmed.png";
const COMPANY = "ZMK Logistics LLC";
const PHONE = "+1 (326) 500-8861";
const EMAIL = "operations@zmk-logistics-llc.com";
const ADDRESS_1 = "2579 Ashcraft Rd,";
const ADDRESS_2 = "Dayton, OH 45414";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.2 } }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.5 } }
};

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', href: '/' },
    { name: 'Escrow', href: '/escrow' },
    { name: 'About', href: '/about' },
    { name: 'Contact', href: '/contact' },
  ];

  const isActive = (href) => href === '/' ? location.pathname === '/' : location.pathname === href;

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-shadow duration-500 nav-light ${
        isScrolled ? 'shadow-md' : ''
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          <Link to="/" data-testid="logo-link" className="flex items-center group">
            <motion.img
              whileHover={{ scale: 1.04 }}
              src={LOGO_URL}
              alt={COMPANY}
              className="h-12 md:h-14 w-auto"
            />
          </Link>

          <div className="hidden md:flex items-center gap-10">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                data-testid={`nav-${link.name.toLowerCase()}`}
                className={`text-sm font-bold tracking-wide uppercase transition-colors duration-300 relative ${
                  isActive(link.href) ? 'text-[#1E9E4F]' : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                {link.name}
                {isActive(link.href) && (
                  <motion.div
                    layoutId="nav-indicator"
                    className="absolute -bottom-2 left-0 right-0 h-0.5 bg-[#1E9E4F]"
                  />
                )}
              </Link>
            ))}
            <Link
              to="/contact"
              data-testid="nav-get-quote-btn"
              className="btn-lift bg-[#1E9E4F] text-white px-7 py-3 rounded-full text-sm font-bold tracking-wide uppercase hover:bg-[#17803F]"
            >
              Get a Rate
            </Link>
          </div>

          <button
            data-testid="mobile-menu-btn"
            className="md:hidden text-gray-900 p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden pb-8 border-t border-gray-200"
            >
              <div className="flex flex-col gap-6 pt-8">
                {navLinks.map((link) => (
                  <Link
                    key={link.name}
                    to={link.href}
                    data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                    className={`text-sm font-bold tracking-wide uppercase ${
                      isActive(link.href) ? 'text-[#1E9E4F]' : 'text-gray-500'
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {link.name}
                  </Link>
                ))}
                <Link
                  to="/contact"
                  className="bg-[#1E9E4F] text-white px-6 py-4 rounded-full text-sm font-bold tracking-wide uppercase text-center"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Get a Rate
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};

// Animated Counter Component (supports decimals like 99.8)
const AnimatedCounter = ({ value, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const decimals = value.includes('.') ? value.split('.')[1].length : 0;

  useEffect(() => {
    const target = parseFloat(value);
    const duration = 2000;
    const steps = 60;
    const increment = target / steps;
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(current);
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [value]);

  return <span>{decimals > 0 ? count.toFixed(decimals) : Math.floor(count)}{suffix}</span>;
};

// Footer Component
const Footer = () => {
  return (
    <footer data-testid="footer" className="relative bg-white border-t border-gray-200">
      {/* Marquee */}
      <div className="py-6 border-b border-gray-100 overflow-hidden">
        <Marquee gradient={false} speed={50} className="overflow-hidden">
          <span className="font-heading text-[100px] font-black text-gray-900/[0.04] uppercase tracking-tighter mx-8">
            ZMK LOGISTICS • SAFE • SECURE • FAST • RELIABLE •
          </span>
        </Marquee>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
        <div className="grid md:grid-cols-4 gap-12">
          <div className="md:col-span-2">
            <img
              src={LOGO_URL}
              alt={COMPANY}
              className="h-20 w-auto mb-8"
            />
            <p className="text-gray-500 leading-relaxed max-w-md">
              At {COMPANY}, we understand that efficient and reliable logistics are the backbone
              of any successful business. With over 20 years of experience, we deliver excellence every time.
            </p>
          </div>

          <div>
            <h4 className="font-heading text-[#1E9E4F] text-sm font-bold tracking-widest uppercase mb-6">Quick Links</h4>
            <ul className="space-y-4">
              {[{ name: 'Home', href: '/' }, { name: 'Escrow', href: '/escrow' }, { name: 'About', href: '/about' }, { name: 'Contact', href: '/contact' }].map((link) => (
                <li key={link.name}>
                  <Link to={link.href} className="text-gray-500 hover:text-[#1E9E4F] transition-colors text-sm">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-heading text-[#1E9E4F] text-sm font-bold tracking-widest uppercase mb-6">Contact</h4>
            <ul className="space-y-4 text-gray-500 text-sm">
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-[#1E9E4F]" />
                {EMAIL}
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-[#1E9E4F]" />
                {PHONE}
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-[#1E9E4F] mt-0.5" />
                <span>{ADDRESS_1}<br />{ADDRESS_2}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-gray-100 py-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-400 text-sm">© 2026 {COMPANY}. All rights reserved.</p>
          <div className="flex gap-8">
            <a href="#" className="text-gray-400 hover:text-[#1E9E4F] text-sm transition-colors">Privacy Policy</a>
            <a href="#" className="text-gray-400 hover:text-[#1E9E4F] text-sm transition-colors">Terms of Service</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// ==================== HOME PAGE ====================
const HomePage = () => {
  const stats = [
    { value: '1580', suffix: '+', label: 'Happy Clients' },
    { value: '1650', suffix: '+', label: 'Deliveries' },
    { value: '99.8', suffix: '%', label: 'On-Time Rate' },
    { value: '20', suffix: '+', label: 'Years' },
  ];

  const services = [
    { icon: Truck, title: 'Freight Shipping', description: 'National freight solutions for businesses of all sizes with real-time tracking.' },
    { icon: Settings, title: 'Logistics Consulting', description: 'Tailored solutions to streamline your logistics processes and reduce costs.' },
    { icon: Zap, title: 'Supply Chain Tech', description: 'Cutting-edge technology for unparalleled efficiency and visibility.' },
    { icon: Shield, title: 'Safe & Secure Payments', description: 'Advanced security protocols protect your shipments, and every payment is processed through encrypted, secure gateways — with escrow protection available so your funds are only released when delivery terms are met.' },
  ];

  const processSteps = [
    { num: '01', title: 'Transport Coordination', desc: 'Our team schedules your shipment using our own trucks to ensure safe and efficient transport.', icon: Settings },
    { num: '02', title: 'Pickup & Transport', desc: 'Your vehicle or equipment is picked up and transported directly by ZMK Logistics trucks, with care every step of the way.', icon: Truck },
    { num: '03', title: 'Safe Delivery', desc: 'Your shipment arrives at its destination safely and on time, handled entirely by our experienced team.', icon: CheckCircle2 },
  ];

  return (
    <div data-testid="home-page" className="overflow-hidden">
      {/* Hero Section */}
      <section data-testid="hero-section" className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
              className="space-y-8"
            >
              <motion.span
                variants={fadeInUp}
                className="inline-block bg-[#1E9E4F]/10 text-[#1E9E4F] text-xs font-bold tracking-[0.2em] uppercase px-4 py-2 rounded-full"
              >
                Next-Gen Logistics
              </motion.span>

              <motion.h1
                variants={fadeInUp}
                data-testid="hero-headline"
                className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[0.95] text-gray-900"
              >
                ZMK
                <br />
                <span className="text-[#1E9E4F]">Logistics</span>
              </motion.h1>

              <motion.p
                variants={fadeInUp}
                className="text-lg text-gray-500 leading-relaxed max-w-lg"
              >
                Experience the future of logistics. Seamless vehicle and equipment transport
                with cutting-edge technology and unmatched reliability.
              </motion.p>

              <motion.div variants={fadeInUp} className="flex flex-wrap gap-4 pt-4">
                <Link
                  to="/contact"
                  data-testid="hero-get-started-btn"
                  className="btn-lift group bg-[#1E9E4F] text-white px-9 py-4 rounded-full text-sm font-bold tracking-wide uppercase hover:bg-[#17803F] flex items-center gap-3"
                >
                  Get Started
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/about"
                  className="border border-gray-300 text-gray-800 px-9 py-4 rounded-full text-sm font-bold tracking-wide uppercase hover:border-[#1E9E4F] hover:text-[#1E9E4F] transition-colors duration-300"
                >
                  Learn More
                </Link>
              </motion.div>

              <motion.div variants={fadeInUp} data-testid="hero-trust-badges" className="flex flex-wrap gap-x-8 gap-y-3 pt-6">
                {[
                  { icon: Lock, text: 'Secure Escrow Payments' },
                  { icon: Shield, text: 'Fully Insured Transport' },
                  { icon: CheckCircle2, text: '99.8% On-Time Delivery' },
                ].map((badge, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-gray-600 font-medium">
                    <badge.icon className="w-4 h-4 text-[#1E9E4F]" strokeWidth={2} />
                    {badge.text}
                  </div>
                ))}
              </motion.div>
            </motion.div>

            {/* Hero image with floating stat card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="relative"
            >
              <div className="rounded-3xl overflow-hidden shadow-xl border border-gray-200">
                <img
                  src="/hero-car-hauler.jpg"
                  alt="ZMK Logistics car hauler transporting vehicles on the highway"
                  className="w-full h-[420px] lg:h-[520px] object-cover"
                />
              </div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-lg border border-gray-200 p-6 flex items-center gap-4"
              >
                <div className="w-12 h-12 bg-[#1E9E4F]/10 rounded-full flex items-center justify-center">
                  <Truck className="w-6 h-6 text-[#1E9E4F]" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="font-heading text-2xl font-black text-gray-900">Nationwide</div>
                  <div className="text-xs text-gray-500 uppercase tracking-widest">Coverage</div>
                </div>
              </motion.div>
            </motion.div>
          </div>

          {/* Stats Row */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="mt-24 bg-white rounded-3xl border border-gray-200 shadow-sm grid grid-cols-2 md:grid-cols-4"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                variants={scaleIn}
                data-testid={`stat-${index}`}
                className="stat-item p-8 md:p-10 text-center"
              >
                <div className="font-heading text-4xl md:text-5xl font-black text-gray-900">
                  <AnimatedCounter value={stat.value} suffix={stat.suffix} />
                </div>
                <div className="text-[#1E9E4F] text-xs font-bold tracking-[0.2em] uppercase mt-3">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Services Bento Grid */}
      <section className="py-28 relative bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="mb-16"
          >
            <motion.span variants={fadeInUp} className="text-[#1E9E4F] text-xs font-bold tracking-[0.3em] uppercase">
              Our Services
            </motion.span>
            <motion.h2 variants={fadeInUp} className="font-heading text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-gray-900 mt-4">
              What We <span className="text-[#1E9E4F]">Deliver</span>
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-12 gap-6"
          >
            {services.map((service, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                data-testid={`service-card-${index}`}
                className={`card-clean p-10 group cursor-pointer ${
                  index === 0 ? 'md:col-span-8' : index === 1 ? 'md:col-span-4' : index === 2 ? 'md:col-span-4' : 'md:col-span-8'
                }`}
              >
                <div className="w-14 h-14 bg-[#1E9E4F]/10 rounded-xl flex items-center justify-center mb-8 group-hover:bg-[#1E9E4F]/20 group-hover:scale-110 transition-transform duration-500">
                  <service.icon className="w-7 h-7 text-[#1E9E4F]" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-2xl font-bold tracking-tight text-gray-900 mb-3 group-hover:text-[#1E9E4F] transition-colors">
                  {service.title}
                </h3>
                <p className="text-gray-500 leading-relaxed">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-28 relative bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-20"
          >
            <motion.span variants={fadeInUp} className="text-[#1E9E4F] text-xs font-bold tracking-[0.3em] uppercase">
              How It Works
            </motion.span>
            <motion.h2 variants={fadeInUp} className="font-heading text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-gray-900 mt-4">
              Our <span className="text-[#1E9E4F]">Process</span>
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-3 gap-8 relative"
          >
            <div className="hidden md:block absolute top-24 left-[20%] right-[20%] h-0.5 bg-gradient-to-r from-transparent via-[#1E9E4F]/30 to-transparent" />

            {processSteps.map((step, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="relative text-center group"
              >
                <div className="relative inline-block mb-8">
                  <div className="w-20 h-20 bg-[#1E9E4F] rounded-full flex items-center justify-center mx-auto group-hover:scale-110 transition-transform duration-500 shadow-lg shadow-[#1E9E4F]/25">
                    <step.icon className="w-10 h-10 text-white" strokeWidth={1.5} />
                  </div>
                  <span className="absolute -top-2 -right-2 text-[#1E9E4F] text-xs font-bold tracking-widest bg-white border border-gray-200 rounded-full px-2 py-1 shadow-sm">
                    {step.num}
                  </span>
                </div>
                <h3 className="font-heading text-xl font-bold tracking-tight text-gray-900 mb-4">
                  {step.title}
                </h3>
                <p className="text-gray-500 leading-relaxed max-w-xs mx-auto">
                  {step.desc}
                </p>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-16"
          >
            <Link
              to="/contact"
              className="btn-lift inline-flex items-center gap-3 bg-[#1E9E4F] text-white px-9 py-4 rounded-full text-sm font-bold tracking-wide uppercase hover:bg-[#17803F]"
            >
              Get a Rate
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-28 relative bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-20"
          >
            <motion.span variants={fadeInUp} className="text-[#1E9E4F] text-xs font-bold tracking-[0.3em] uppercase">
              Testimonials
            </motion.span>
            <motion.h2 variants={fadeInUp} className="font-heading text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-gray-900 mt-4">
              Client <span className="text-[#1E9E4F]">Reviews</span>
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-3 gap-8"
          >
            {[
              { name: 'Robert Davis', role: 'CEO, TechCorp', content: 'ZMK Logistics transformed our supply chain. Seamless coordination and on-time deliveries every single time.' },
              { name: 'Sandra Hill', role: 'Operations Director', content: "We've partnered with them for years. Their expertise and reliability make them an invaluable strategic partner." },
              { name: 'Bruce Miller', role: 'Supply Chain Manager', content: "Exceptional service. Their dedication to safety and transparent communication sets them apart from the competition." },
            ].map((testimonial, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                data-testid={`testimonial-${index}`}
                className="card-clean p-10"
              >
                <div className="flex gap-1 mb-6">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-[#1E9E4F] text-[#1E9E4F]" />
                  ))}
                </div>
                <p className="text-gray-600 leading-relaxed mb-8">"{testimonial.content}"</p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#1E9E4F]/10 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-[#1E9E4F]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="font-heading font-bold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-500">{testimonial.role}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Secure Payments Banner */}
      <section data-testid="secure-payments-banner" className="py-20 bg-[#FAFAFA] border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-3xl border border-gray-200 shadow-sm p-10 md:p-14 flex flex-col md:flex-row items-center gap-10"
          >
            <div className="w-20 h-20 bg-[#1E9E4F]/10 rounded-2xl flex items-center justify-center flex-shrink-0">
              <Lock className="w-10 h-10 text-[#1E9E4F]" strokeWidth={1.5} />
            </div>
            <div className="flex-1 text-center md:text-left">
              <h3 className="font-heading text-2xl md:text-3xl font-black tracking-tight text-gray-900">
                100% Secure Payments, Guaranteed
              </h3>
              <p className="text-gray-500 leading-relaxed mt-3 max-w-2xl">
                Every transaction is encrypted and processed through secure payment gateways.
                With our escrow service, your funds stay protected in a trust account and are
                only released once your vehicle or equipment is delivered and accepted.
              </p>
            </div>
            <Link
              to="/escrow"
              data-testid="secure-payments-learn-more-btn"
              className="btn-lift bg-[#1E9E4F] text-white px-8 py-4 rounded-full text-sm font-bold tracking-wide uppercase hover:bg-[#17803F] whitespace-nowrap"
            >
              How Escrow Works
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-28 relative bg-[#0D5F2D] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, #fff 0, #fff 1px, transparent 0, transparent 50%)',
            backgroundSize: '20px 20px'
          }} />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center relative z-10">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-white"
          >
            Ready to Ship?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-white/70 text-lg mt-6 max-w-2xl mx-auto"
          >
            Get a quote today and experience logistics done right.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <Link
              to="/contact"
              className="btn-lift inline-flex items-center gap-3 bg-white text-[#0D5F2D] px-10 py-5 rounded-full text-sm font-bold tracking-wide uppercase mt-10"
            >
              Get a Quote
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

// ==================== ESCROW PAGE ====================
const EscrowPage = () => {
  const escrowSteps = [
    { num: '01', title: 'Agreement on Terms', desc: `The buyer and seller agree to transaction terms, initiating the process with ${COMPANY}` },
    { num: '02', title: 'Buyer Payment', desc: 'The buyer submits payment through an approved method. Upon verification, the seller is notified that funds are secured.' },
    { num: '03', title: 'Shipment', desc: 'After payment verification, ZMK Logistics ships the merchandise and provides tracking information.' },
    { num: '04', title: 'Buyer Inspection', desc: 'The buyer has a specified period to inspect the merchandise and can either accept or reject it.' },
    { num: '05', title: 'Payment to Seller', desc: 'Funds are released from escrow to the seller once the buyer accepts the merchandise.' },
    { num: '06', title: 'Buyer Rejection', desc: 'If rejected, the buyer is refunded in full within 24 hours, with free return shipping.' },
  ];

  return (
    <div data-testid="escrow-page" className="pt-20 overflow-hidden">
      {/* Hero */}
      <section className="py-24 relative bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="inline-block bg-[#1E9E4F]/10 text-[#1E9E4F] text-xs font-bold tracking-[0.2em] uppercase px-4 py-2 rounded-full"
          >
            Secure Transactions
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-heading text-5xl md:text-6xl lg:text-7xl font-black tracking-tight text-gray-900 mt-6"
          >
            Escrow <span className="text-[#1E9E4F]">Services</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-gray-500 mt-6 max-w-2xl mx-auto"
          >
            Your funds are protected in a secure trust account until the transaction is complete.
          </motion.p>
        </div>
      </section>

      {/* What is Escrow */}
      <section className="py-24 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              <motion.span variants={fadeInUp} className="text-[#1E9E4F] text-xs font-bold tracking-[0.3em] uppercase">
                Understanding Escrow
              </motion.span>
              <motion.h2 variants={fadeInUp} className="font-heading text-4xl md:text-5xl font-black tracking-tight text-gray-900 mt-4">
                What is <span className="text-[#1E9E4F]">Escrow?</span>
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-gray-500 text-lg mt-6 leading-relaxed">
                Escrow is a financial arrangement where ZMK Logistics holds and regulates
                payment of funds required for two parties in a transaction. Your money is kept in a
                secure escrow account which is only released when all terms are met.
              </motion.p>
              <motion.div variants={fadeInUp} className="mt-8 p-6 bg-[#1E9E4F]/5 border border-[#1E9E4F]/30 rounded-2xl">
                <p className="text-gray-900">
                  <span className="text-[#1E9E4F] font-bold">NOTICE:</span> The party that activates Escrow pays for the service.
                </p>
              </motion.div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease: 'easeOut' }}
              className="relative"
              data-testid="escrow-process-infographic"
            >
              <div className="relative rounded-3xl overflow-hidden border border-gray-200 bg-white shadow-xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_cargo-command-27/artifacts/8be8lqkf_IMG-1412.png"
                  alt="Secure Escrow Process — Agreement, Funds, Delivery, Payment"
                  className="w-full h-auto block"
                  loading="lazy"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-24 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-20"
          >
            <motion.span variants={fadeInUp} className="text-[#1E9E4F] text-xs font-bold tracking-[0.3em] uppercase">
              The Process
            </motion.span>
            <motion.h2 variants={fadeInUp} className="font-heading text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-gray-900 mt-4">
              How It <span className="text-[#1E9E4F]">Works</span>
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {escrowSteps.map((step, index) => (
              <motion.div
                key={index}
                data-testid={`escrow-step-${index}`}
                variants={fadeInUp}
                className="card-clean p-8 relative group"
              >
                <span className="font-heading text-[#1E9E4F] text-6xl font-black opacity-10 absolute top-4 right-4 group-hover:opacity-25 transition-opacity">
                  {step.num}
                </span>
                <h3 className="font-heading text-xl font-bold tracking-tight text-gray-900 mb-4 relative z-10">
                  {step.title}
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed relative z-10">
                  {step.desc}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Security Section */}
      <section className="py-24 bg-[#0D5F2D]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row items-center justify-center gap-8 text-center md:text-left">
            <Lock className="w-20 h-20 text-white" strokeWidth={1} />
            <div>
              <h3 className="font-heading text-3xl font-bold tracking-tight mb-4 text-white">
                100% Secure Transactions
              </h3>
              <p className="text-white/70 max-w-xl">
                All transactions are encrypted and processed through secure payment gateways.
                Your financial information is never stored on our servers.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// ==================== ABOUT PAGE ====================
const AboutPage = () => {
  const features = [
    { icon: CheckCircle2, title: 'Tailored Solutions', desc: 'Customized logistics solutions that align precisely with your specific needs.' },
    { icon: Clock, title: 'Reliability', desc: 'Dependability is the cornerstone of our service. We deliver on time, every time.' },
    { icon: Zap, title: 'Advanced Tech', desc: 'Cutting-edge technology for real-time tracking and automated management.' },
  ];

  return (
    <div data-testid="about-page" className="pt-20 overflow-hidden">
      <section className="py-24 relative bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="inline-block bg-[#1E9E4F]/10 text-[#1E9E4F] text-xs font-bold tracking-[0.2em] uppercase px-4 py-2 rounded-full"
          >
            About Us
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-heading text-5xl md:text-6xl lg:text-7xl font-black tracking-tight text-gray-900 mt-6"
          >
            Why <span className="text-[#1E9E4F]">Choose Us</span>
          </motion.h1>
        </div>
      </section>

      <section className="py-24 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-xl border border-gray-200">
                <img
                  src="https://images.pexels.com/photos/4487363/pexels-photo-4487363.jpeg"
                  alt="Modern logistics warehouse"
                  className="w-full h-[480px] object-cover"
                />
              </div>
              <div className="absolute bottom-6 left-6 bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
                <div className="font-heading text-4xl font-black text-[#1E9E4F]">20+</div>
                <div className="text-xs tracking-widest uppercase text-gray-500 font-bold mt-1">Years of Excellence</div>
              </div>
            </div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
              className="space-y-8"
            >
              <motion.h2 variants={fadeInUp} className="font-heading text-4xl md:text-5xl font-black tracking-tight text-gray-900">
                Best Logistics <span className="text-[#1E9E4F]">For Your Business</span>
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-gray-500 text-lg leading-relaxed">
                At {COMPANY}, we understand that efficient and reliable logistics are
                the backbone of any successful business. With cutting-edge technology and a dedicated team,
                we deliver comprehensive logistics solutions tailored to your unique needs.
              </motion.p>

              <motion.div variants={staggerContainer} className="space-y-4">
                {features.map((feature, index) => (
                  <motion.div
                    key={index}
                    variants={fadeInUp}
                    className="card-clean flex gap-4 p-6"
                  >
                    <div className="w-12 h-12 bg-[#1E9E4F]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <feature.icon className="w-6 h-6 text-[#1E9E4F]" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="font-heading font-bold tracking-tight text-gray-900 mb-2">{feature.title}</h3>
                      <p className="text-gray-500 text-sm">{feature.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 bg-[#1E9E4F]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: '1580+', label: 'Clients' },
              { value: '1650+', label: 'Deliveries' },
              { value: '99.8%', label: 'On-Time' },
              { value: '20+', label: 'Years' },
            ].map((stat, index) => (
              <div key={index}>
                <div className="font-heading text-4xl md:text-5xl font-black text-white">{stat.value}</div>
                <div className="text-xs tracking-widest uppercase text-white/70 font-bold mt-2">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

// ==================== CONTACT PAGE ====================
const ContactPage = () => {
  const [formData, setFormData] = useState({
    origin: '', destination: '', fullName: '', phone: '', email: '', message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
  };

  return (
    <div data-testid="contact-page" className="pt-20 overflow-hidden">
      <section className="py-24 relative bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="inline-block bg-[#1E9E4F]/10 text-[#1E9E4F] text-xs font-bold tracking-[0.2em] uppercase px-4 py-2 rounded-full"
          >
            Contact Us
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-heading text-5xl md:text-6xl lg:text-7xl font-black tracking-tight text-gray-900 mt-6"
          >
            Get a <span className="text-[#1E9E4F]">Rate</span>
          </motion.h1>
        </div>
      </section>

      <section className="py-24 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
              className="bg-white rounded-3xl border border-gray-200 shadow-md p-10"
            >
              <motion.h2 variants={fadeInUp} className="font-heading text-2xl font-bold tracking-tight text-gray-900 mb-8">
                Request a Quote
              </motion.h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-xs text-gray-500 font-bold tracking-widest uppercase mb-2 block">Origin</label>
                    <input
                      type="text"
                      data-testid="quote-origin-input"
                      placeholder="City or State"
                      className="w-full px-5 py-4 rounded-xl transition-colors"
                      value={formData.origin}
                      onChange={(e) => setFormData({...formData, origin: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 font-bold tracking-widest uppercase mb-2 block">Destination</label>
                    <input
                      type="text"
                      data-testid="quote-destination-input"
                      placeholder="City or State"
                      className="w-full px-5 py-4 rounded-xl transition-colors"
                      value={formData.destination}
                      onChange={(e) => setFormData({...formData, destination: e.target.value})}
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-xs text-gray-500 font-bold tracking-widest uppercase mb-2 block">Full Name</label>
                    <input
                      type="text"
                      data-testid="quote-name-input"
                      placeholder="Your name"
                      className="w-full px-5 py-4 rounded-xl transition-colors"
                      value={formData.fullName}
                      onChange={(e) => setFormData({...formData, fullName: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 font-bold tracking-widest uppercase mb-2 block">Phone</label>
                    <input
                      type="tel"
                      data-testid="quote-phone-input"
                      placeholder="Your phone"
                      className="w-full px-5 py-4 rounded-xl transition-colors"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-bold tracking-widest uppercase mb-2 block">Email</label>
                  <input
                    type="email"
                    data-testid="quote-email-input"
                    placeholder="your@email.com"
                    className="w-full px-5 py-4 rounded-xl transition-colors"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-bold tracking-widest uppercase mb-2 block">Message</label>
                  <textarea
                    data-testid="quote-message-input"
                    placeholder="Tell us about your shipment..."
                    rows={4}
                    className="w-full px-5 py-4 rounded-xl transition-colors resize-none"
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                  />
                </div>
                <button
                  type="submit"
                  data-testid="quote-submit-btn"
                  className="btn-lift w-full bg-[#1E9E4F] text-white py-5 rounded-full text-sm font-bold tracking-wide uppercase hover:bg-[#17803F] flex items-center justify-center gap-3"
                >
                  Get Quote
                  <ArrowRight className="w-5 h-5" />
                </button>
              </form>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
              className="space-y-8"
            >
              <motion.h2 variants={fadeInUp} className="font-heading text-3xl font-bold tracking-tight text-gray-900">
                Contact Info
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-gray-500 leading-relaxed">
                Have questions? Reach out to us directly. Our team is ready to assist with all your logistics needs.
              </motion.p>

              <motion.div variants={staggerContainer} className="space-y-4">
                {[
                  { icon: Mail, label: 'Email', value: EMAIL },
                  { icon: Phone, label: 'Phone', value: PHONE, extra: 'Mon-Fri: 9am-5pm, Sat: 9am-2pm' },
                  { icon: MapPin, label: 'Address', value: `${ADDRESS_1} ${ADDRESS_2}` },
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    variants={fadeInUp}
                    className="card-clean flex items-start gap-4 p-6"
                  >
                    <item.icon className="w-6 h-6 text-[#1E9E4F]" strokeWidth={1.5} />
                    <div>
                      <div className="text-xs text-[#1E9E4F] font-bold tracking-widest uppercase mb-1">{item.label}</div>
                      <div className="text-gray-900">{item.value}</div>
                      {item.extra && <div className="text-gray-500 text-sm mt-1">{item.extra}</div>}
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Scroll to top component
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

// Main App
function App() {
  return (
    <BrowserRouter>
      <div className="min-h-screen bg-[#FAFAFA] text-gray-900">
        <div className="grain-overlay" />
        <Navigation />
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/escrow" element={<EscrowPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
        </Routes>
        <Footer />
      </div>
    </BrowserRouter>
  );
}

export default App;
